import { Routes } from 'constants/index'
import { observer } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthStore, NavStore } from 'stores'
import { useInstance } from 'util/di'
import ResourceCounter from './ResourceCounter'

import { gevent } from 'helpers/gtag'

import { UsersResourceCounts } from 'types/api'

import * as S from './styled'

export type SubNavItem = {
  path: string
  label: string
  count?: (keyof UsersResourceCounts)[]
  admin_count?: number
  locked?: boolean
}

type Props = {
  navItems: SubNavItem[]
  refresh?: boolean
}

const SubNav = ({
  navItems,
  refresh = false,
  ...rest
}: Props): JSX.Element | null => {
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useInstance<AuthStore>('AuthStore')
  const { requestedCount, getRequestedCount } =
    useInstance<NavStore>('NavStore')
  const isAdmin = useMemo(() => Boolean(session?.isAdmin), [session])
  const handleNav = (path: string) => {
    gevent('navigation', 'Header', { destination_url: path })
    if (refresh) {
      window.location.href = path
    } else {
      navigate(path)
    }
  }

  useEffect(() => {
    if (!isAdmin) return

    getRequestedCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin])

  useEffect(() => {
    if (isAdmin && location.pathname === Routes.Requested) {
      getRequestedCount()
    }
  }, [getRequestedCount, isAdmin, location.pathname])

  return (
    <S.Container square elevation={0} data-testid="sub-nav" {...rest}>
      {navItems.map(navItem => (
        <S.StyledNavLink
          key={navItem.path}
          onClick={navItem.locked ? () => {} : () => handleNav(navItem.path)}
          to={navItem.path}
          $locked={navItem.locked}
          className={({ isActive }: { isActive: boolean }) =>
            isActive ? 'active' : undefined
          }
        >
          {navItem.locked && <S.LockedIcon />}
          {navItem.label}

          {navItem.count ? (
            <ResourceCounter count={navItem.count} />
          ) : navItem.label.toLowerCase() === 'requested' && requestedCount ? (
            <S.Count>({requestedCount.toLocaleString()})</S.Count>
          ) : navItem.admin_count &&
            navItem.label.toLowerCase() !== 'requested' ? (
            <S.Count>({navItem.admin_count.toLocaleString()})</S.Count>
          ) : null}
        </S.StyledNavLink>
      ))}
    </S.Container>
  )
}

export default observer(SubNav)
